import React from 'react'
import {
    Box,
    Button,
    Chip,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined'
import LockOutlinedIcon from '@material-ui/icons/LockOutlined'
import SwapHorizIcon from '@material-ui/icons/SwapHoriz'

import {
    SeasonConfigBundle,
    bundleReadiness,
    bundleStatusLabel,
} from './seasonConfigBundle'
import {
    LEASING_SEASON_CONFIG_CONTENT_MAX_WIDTH,
    formatModifiedLabel,
} from './setupArtifacts'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        width: '100%',
        maxWidth: LEASING_SEASON_CONFIG_CONTENT_MAX_WIDTH,
        alignSelf: 'center',
        flexShrink: 0,
    },
    rootPageHeader: {
        maxWidth: 'none',
        alignSelf: 'stretch',
    },
    bar: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'stretch',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        padding: theme.spacing(1.25, 1.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        backgroundColor: theme.palette.background.paper,
    },
    barPageHeader: {
        padding: 0,
        border: 'none',
        borderRadius: 0,
        backgroundColor: 'transparent',
        alignItems: 'flex-start',
    },
    barLocked: {
        backgroundColor: theme.palette.grey[50],
    },
    barLockedPageHeader: {
        backgroundColor: 'transparent',
    },
    main: {
        minWidth: 0,
        flex: 1,
    },
    label: {
        fontSize: '0.6875rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: theme.palette.text.secondary,
    },
    name: {
        fontWeight: 700,
        fontSize: '1.0625rem',
        lineHeight: 1.3,
        marginTop: 2,
    },
    namePageHeader: {
        fontSize: '1.25rem',
        marginTop: 0,
    },
    meta: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        marginTop: 2,
        lineHeight: 1.4,
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.5),
        marginTop: theme.spacing(0.75),
    },
    side: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        flexShrink: 0,
        gap: theme.spacing(1),
    },
    sidePageHeader: {
        justifyContent: 'flex-start',
        paddingTop: 2,
    },
    lockNote: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        maxWidth: 280,
        textAlign: 'right',
        lineHeight: 1.4,
    },
}))

export const SeasonConfigBundleBar: React.FC<{
    bundle: SeasonConfigBundle
    onChange: () => void
    onCreateCopy?: () => void
    /** pageHeader: flush into the page title row (no card). */
    variant?: 'card' | 'pageHeader'
}> = ({ bundle, onChange, onCreateCopy, variant = 'card' }) => {
    const classes = useStyles()
    const readiness = bundleReadiness(bundle)
    const modified = formatModifiedLabel(bundle.modifiedAt)
    const mandated = !!bundle.orgMandated
    const locked = bundle.isEditable === false
    const statusLabel = locked ? bundleStatusLabel(bundle) : readiness.label
    const isPageHeader = variant === 'pageHeader'

    return (
        <Box
            className={`${classes.root}${
                isPageHeader ? ` ${classes.rootPageHeader}` : ''
            }`}
        >
            <Box
                className={`${classes.bar}${
                    isPageHeader ? ` ${classes.barPageHeader}` : ''
                }${
                    locked
                        ? isPageHeader
                            ? ` ${classes.barLockedPageHeader}`
                            : ` ${classes.barLocked}`
                        : ''
                }`}
            >
                <Box className={classes.main}>
                    {!isPageHeader ? (
                        <Typography className={classes.label}>
                            Selected Leasing Season Config
                        </Typography>
                    ) : null}
                    <Typography
                        className={`${classes.name}${
                            isPageHeader ? ` ${classes.namePageHeader}` : ''
                        }`}
                    >
                        {bundle.name}
                    </Typography>
                    <Typography className={classes.meta}>
                        {bundle.description ??
                            'Property config · seasons apply this whole bundle'}
                        {modified ? ` · ${modified}` : ''}
                    </Typography>
                    <Box className={classes.chips}>
                        <Chip
                            size="small"
                            icon={
                                locked ? (
                                    <LockOutlinedIcon
                                        style={{ fontSize: 14 }}
                                    />
                                ) : undefined
                            }
                            label={statusLabel}
                            color={
                                locked
                                    ? 'default'
                                    : readiness.ready
                                    ? 'primary'
                                    : 'default'
                            }
                            variant={
                                locked || !readiness.ready
                                    ? 'outlined'
                                    : 'default'
                            }
                        />
                        <Chip
                            size="small"
                            label={
                                bundle.createdFrom === 'org'
                                    ? 'Based on org'
                                    : bundle.createdFrom === 'ezos'
                                    ? 'Based on EZOS'
                                    : bundle.createdFrom === 'scratch'
                                    ? 'Scratch'
                                    : 'Property'
                            }
                            variant="outlined"
                        />
                        {mandated ? (
                            <Chip
                                size="small"
                                icon={
                                    <LockOutlinedIcon
                                        style={{ fontSize: 14 }}
                                    />
                                }
                                label="Org mandated"
                                color="primary"
                                variant="outlined"
                            />
                        ) : null}
                    </Box>
                </Box>
                <Box
                    className={`${classes.side}${
                        isPageHeader ? ` ${classes.sidePageHeader}` : ''
                    }`}
                >
                    {!mandated ? (
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<SwapHorizIcon />}
                            onClick={onChange}
                        >
                            Change
                        </Button>
                    ) : null}
                    {locked && onCreateCopy ? (
                        <>
                            <Typography className={classes.lockNote}>
                                This config is locked while an active season
                                uses it. Create a copy to make changes.
                            </Typography>
                            <Button
                                size="small"
                                color="primary"
                                variant="contained"
                                startIcon={<FileCopyOutlinedIcon />}
                                onClick={onCreateCopy}
                            >
                                Create a copy
                            </Button>
                        </>
                    ) : null}
                </Box>
            </Box>
        </Box>
    )
}
